// src/components/GamesHub.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiGrid, FiTarget, FiTrendingUp, FiZap, FiClock, FiShuffle, FiAward } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';

// استيراد الألعاب
import MemoryGameScreen from './MemoryGameScreen';
import TicTacToeGame from './games/TicTacToeGame';
import NumberPuzzleGame from './games/NumberPuzzleGame';
import ColorMatchGame from './games/ColorMatchGame';
import ReflexGame from './games/ReflexGame';

const GamesHub = () => {
  const navigate = useNavigate();
  const { userType } = useAuth();
  const [selectedGame, setSelectedGame] = useState(null);

  const games = [
    {
      id: 'memory',
      name: 'لعبة الذاكرة',
      description: 'اقلب البطاقات واعثر على الأزواج المتطابقة',
      icon: FiGrid,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-gradient-to-br from-purple-50 to-pink-50',
      borderColor: 'border-purple-200',
      difficulty: 'متوسط',
      players: '1',
      emoji: '🎮',
      component: MemoryGameScreen
    },
    {
      id: 'tictactoe',
      name: 'XO (تيك تاك تو)',
      description: 'العبها ضد الكمبيوتر أو مع صديق',
      icon: FiTarget,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200',
      difficulty: 'سهل',
      players: '1-2',
      emoji: '⭕',
      component: TicTacToeGame
    },
    {
      id: 'number-puzzle',
      name: 'لغز الأرقام',
      description: 'رتب الأرقام من 1 إلى 15 بأسرع وقت',
      icon: FiTrendingUp,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
      borderColor: 'border-green-200',
      difficulty: 'صعب',
      players: '1',
      emoji: '🔢',
      component: NumberPuzzleGame
    },
    {
      id: 'color-match',
      name: 'مطابقة الألوان',
      description: 'اختر اللون الصحيح بأسرع وقت ممكن',
      icon: FiZap,
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-gradient-to-br from-orange-50 to-red-50',
      borderColor: 'border-orange-200',
      difficulty: 'سهل',
      players: '1',
      emoji: '🎨',
      component: ColorMatchGame
    },
    {
      id: 'reflex',
      name: 'اختبار سرعة البديهة',
      description: 'اضغط على الهدف بأسرع ما يمكن!',
      icon: FiClock,
      color: 'from-yellow-500 to-amber-600',
      bgColor: 'bg-gradient-to-br from-yellow-50 to-amber-50',
      borderColor: 'border-yellow-200',
      difficulty: 'متوسط',
      players: '1',
      emoji: '⚡',
      component: ReflexGame
    },
    {
      id: 'simon',
      name: 'لعبة سايمون',
      description: 'تذكر تسلسل الألوان وكرره بنفس الترتيب',
      icon: FiShuffle,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50',
      borderColor: 'border-indigo-200',
      difficulty: 'صعب',
      players: '1',
      emoji: '🎯',
      component: null // سيتم إنشاؤها
    }
  ];

  const navigateBack = () => {
    if (selectedGame) {
      setSelectedGame(null);
    } else {
      if (userType === 'provider') {
        navigate('/provider');
      } else {
        navigate('/client');
      }
    }
  };

  // إذا تم اختيار لعبة
  if (selectedGame) {
    const GameComponent = selectedGame.component;
    if (!GameComponent) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900 mb-4">قريباً...</p>
            <button
              onClick={() => setSelectedGame(null)}
              className="bg-gray-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-800 transition"
            >
              العودة
            </button>
          </div>
        </div>
      );
    }
    return <GameComponent onBack={() => setSelectedGame(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tl from-blue-500/10 to-transparent rounded-full blur-3xl"></div>

      {/* Header */}
      <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/20 via-transparent to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={navigateBack}
              className="group flex items-center gap-2 text-white/80 hover:text-white transition-all duration-300 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10"
            >
              <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" size={18} />
              <span className="text-sm font-bold">رجوع</span>
            </button>
            
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/10">
              <FiAward className="text-yellow-400" size={18} />
              <span className="text-sm font-bold">{games.length} لعبة</span>
            </div>
          </div>

          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-4xl md:text-5xl font-black mb-2 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent"
            >
              مركز الألعاب 🎮
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-sm sm:text-base text-gray-300"
            >
              اختر لعبتك المفضلة واستمتع بوقتك!
            </motion.p>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {games.map((game, index) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              onClick={() => setSelectedGame(game)}
              className={`group relative ${game.bgColor} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-2 ${game.borderColor} hover:scale-105 active:scale-95 overflow-hidden`}
            >
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/30 rounded-full blur-2xl -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500"></div>
              
              <div className="relative">
                {/* Game Icon/Emoji */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${game.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-2xl">{game.emoji}</span>
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    <div className="bg-white/50 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-bold text-gray-700">
                      {game.difficulty}
                    </div>
                    <div className="bg-white/50 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-bold text-gray-700 text-center">
                      {game.players} لاعب
                    </div>
                  </div>
                </div>

                {/* Game Info */}
                <h3 className="text-xl font-black text-gray-900 mb-2">
                  {game.name}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  {game.description}
                </p>

                {/* Play Button */}
                <button
                  className={`w-full bg-gradient-to-r ${game.color} text-white py-3 rounded-xl font-bold shadow-md group-hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2`}
                >
                  <FiZap size={18} />
                  <span>العب الآن</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100"
        >
          <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
            <FiAward className="text-yellow-500" size={24} />
            إحصائياتك
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
              <p className="text-2xl font-black text-gray-900">0</p>
              <p className="text-xs text-gray-600 font-bold">ألعاب منجزة</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
              <p className="text-2xl font-black text-gray-900">0</p>
              <p className="text-xs text-gray-600 font-bold">انتصارات</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
              <p className="text-2xl font-black text-gray-900">0</p>
              <p className="text-xs text-gray-600 font-bold">أفضل نتيجة</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200">
              <p className="text-2xl font-black text-gray-900">0</p>
              <p className="text-xs text-gray-600 font-bold">وقت اللعب</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GamesHub;